import * as React from 'react';
import {TypeImplementation} from '@packages/systems/wfdl';
import {useExpressionEditorImplementationContext} from '@packages/systems/ExpressionEditor-utils';
import type {FileRecordType} from '@packages/systems/core/records/FileRecord';

// Shared state machine for the "upload from device" flow used by asset props in
// dynamic rich text (image props and File links). Each caller supplies its own
// `onUploaded` so it can build its own literal shape.
export const useUploadFromDevice = ({
  accept,
  onUploaded,
}: {
  accept?: TypeImplementation.AssetUploadFromDevice['accept'];
  onUploaded: (asset: FileRecordType) => void;
}) => {
  const {env} = useExpressionEditorImplementationContext();
  const uploadFromDevice = env.asset.uploadFromDevice;
  const canUploadFromDevice = typeof uploadFromDevice === 'function';

  // Ignores a resolved upload when the user picked a newer file first, so the
  // latest selection wins the property.
  const latestUploadSeqRef = React.useRef(0);
  const [isUploading, setIsUploading] = React.useState(false);

  const chooseFromDevice = () => {
    const uploadSeq = ++latestUploadSeqRef.current;
    uploadFromDevice?.({
      accept,
      onUploadStart() {
        setIsUploading(true);
      },
      onUploaded(uploadedAsset) {
        if (uploadSeq !== latestUploadSeqRef.current) {
          return;
        }
        setIsUploading(false);
        env.transaction.begin();
        onUploaded(uploadedAsset);
        env.transaction.commit();
      },
      onError() {
        if (uploadSeq !== latestUploadSeqRef.current) {
          return;
        }
        setIsUploading(false);
      },
    });
  };

  return {canUploadFromDevice, isUploading, chooseFromDevice};
};
